import * as PopoverPrimitive from '@radix-ui/react-popover';
import { ChevronsUpDown } from 'lucide-react';
import * as React from 'react';

import { Button } from '@apps/web/components/ui/button';
import {
    Command,
    CommandInput,
    CommandList,
} from '@apps/web/components/ui/command';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@apps/web/components/ui/popover';
import { cn } from '@apps/web/lib/utils';

type TriggerProps = Readonly<
    Omit<
        React.ComponentPropsWithoutRef<'button'>,
        'className' | 'disabled' | 'aria-expanded'
    >
> & {
    readonly 'data-row-click-ignore'?: string;
    readonly 'data-testid'?: string;
};

type CommonComboboxProps = {
    readonly open: boolean;
    readonly onOpenChange: (open: boolean) => void;
    readonly displayValue?: React.ReactNode;
    readonly placeholder?: React.ReactNode;
    readonly disabled?: boolean;
    readonly clearSearchOnClose?: boolean;
    readonly triggerClassName?: string;
    readonly triggerProps?: TriggerProps;
    readonly contentClassName?: string;
    readonly contentSide?: React.ComponentProps<typeof PopoverContent>['side'];
    readonly contentAlign?: React.ComponentProps<
        typeof PopoverContent
    >['align'];
    readonly contentSideOffset?: number;
    readonly commandProps?: React.ComponentProps<typeof Command>;
    readonly commandListProps?: React.ComponentProps<typeof CommandList>;
    readonly children: React.ReactNode;
    readonly matchTriggerWidth?: boolean;
    readonly fullWidth?: boolean;
    readonly showCaret?: boolean;
    readonly scrollLockRef?: React.RefObject<HTMLElement | null>;
};

type SearchableComboboxProps =
    | {
          readonly hideSearch?: false;
          readonly searchValue: string;
          readonly onSearchValueChange: (value: string) => void;
          readonly searchPlaceholder?: string;
      }
    | {
          readonly hideSearch: true;
          readonly searchValue?: string;
          readonly onSearchValueChange?: (value: string) => void;
          readonly searchPlaceholder?: string;
      };

type BaseComboboxProps = CommonComboboxProps & SearchableComboboxProps;

export function Combobox({
    open,
    onOpenChange,
    displayValue,
    placeholder = 'Select an option...',
    disabled = false,
    searchValue,
    onSearchValueChange,
    searchPlaceholder = 'Search...',
    hideSearch = false,
    clearSearchOnClose = true,
    triggerClassName,
    triggerProps,
    contentClassName,
    contentSide,
    contentAlign,
    contentSideOffset,
    commandProps,
    commandListProps,
    children,
    matchTriggerWidth = true,
    fullWidth = true,
    showCaret = true,
    scrollLockRef,
}: BaseComboboxProps) {
    React.useEffect(() => {
        if (!open || typeof document === 'undefined') {
            return;
        }

        const scrollLockTarget = scrollLockRef?.current ?? null;
        const target = scrollLockTarget ?? document.body;
        if (!target) {
            return;
        }

        const isCustomTarget = scrollLockTarget !== null;
        const isBody = target instanceof HTMLBodyElement;

        const computedStyle = window.getComputedStyle(target);
        const computedOverflow = computedStyle.overflow;
        const computedOverflowX = computedStyle.overflowX;
        const computedOverflowY = computedStyle.overflowY;
        const originalOverflow = target.style.overflow;
        const originalOverflowX = isBody ? target.style.overflowX : '';
        const originalOverflowY = isBody ? target.style.overflowY : '';
        const originalPaddingRight = isBody ? target.style.paddingRight : '';
        const scrollbarWidth =
            window.innerWidth - document.documentElement.clientWidth;

        target.style.overflow = 'hidden';
        if (isBody && scrollbarWidth > 0) {
            target.style.paddingRight = `${scrollbarWidth}px`;
        }

        return () => {
            if (isCustomTarget) {
                target.style.overflow = originalOverflow || computedOverflow;
                if (isBody) {
                    target.style.overflowX =
                        originalOverflowX || computedOverflowX;
                    target.style.overflowY =
                        originalOverflowY || computedOverflowY;
                }
            } else {
                target.style.overflow = originalOverflow;
            }

            if (isBody) {
                target.style.paddingRight = originalPaddingRight;
            }
        };
    }, [open, scrollLockRef]);

    React.useEffect(() => {
        if (!open && clearSearchOnClose && onSearchValueChange) {
            onSearchValueChange('');
        }
    }, [open, clearSearchOnClose, onSearchValueChange]);

    const hasDisplayValue =
        displayValue !== null &&
        displayValue !== undefined &&
        !(typeof displayValue === 'string' && displayValue.length === 0);

    return (
        <Popover open={open} onOpenChange={onOpenChange}>
            <PopoverTrigger asChild={true}>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className={cn(
                        // `min-w-0` keeps a long display value from growing the
                        // trigger past its container's width.
                        fullWidth ? 'w-full min-w-0' : 'w-auto max-w-full',
                        showCaret ? 'justify-between' : 'justify-start',
                        triggerClassName,
                    )}
                    disabled={disabled}
                    {...triggerProps}
                >
                    <span
                        className={cn(
                            'flex-1 truncate text-left',
                            !hasDisplayValue && 'text-muted-foreground',
                        )}
                    >
                        {hasDisplayValue ? displayValue : placeholder}
                    </span>
                    {showCaret ? (
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    ) : null}
                </Button>
            </PopoverTrigger>
            {open ? (
                <PopoverPrimitive.Portal forceMount={true}>
                    <div
                        role="presentation"
                        className="fixed inset-0 z-40"
                        data-row-click-ignore="true"
                        onPointerDown={(event) => {
                            event.stopPropagation();
                            event.preventDefault();
                            onOpenChange(false);
                        }}
                        onClick={(event) => {
                            event.stopPropagation();
                            event.preventDefault();
                        }}
                    />
                </PopoverPrimitive.Portal>
            ) : null}
            <PopoverContent
                side={contentSide}
                align={contentAlign}
                sideOffset={contentSideOffset}
                className={cn(
                    'p-0',
                    matchTriggerWidth && 'w-(--radix-popover-trigger-width)',
                    contentClassName,
                )}
            >
                <Command {...commandProps}>
                    {!hideSearch && (
                        <CommandInput
                            placeholder={
                                typeof searchPlaceholder === 'string'
                                    ? searchPlaceholder
                                    : undefined
                            }
                            value={searchValue ?? ''}
                            onValueChange={onSearchValueChange}
                        />
                    )}
                    <CommandList {...commandListProps}>{children}</CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
